<template>
  <div class="app-layout">
    <!-- Sidebar Component -->
    <Sidebar @sidebar-collapse="handleSidebarCollapse" />
    
    <!-- Main Content -->
    <div class="files-list-container" :class="{ 'sidebar-collapsed': sidebarCollapsed }">
    <!-- Header Section -->
    <div class="header-section mb-6">
      <h2 class="text-2xl font-bold text-gray-800 mb-2">Files Management</h2>
      <p class="text-gray-600">Upload, browse and manage files from the CDN</p>
    </div>

    <!-- Upload Section -->
    <div class="upload-section bg-white rounded-lg shadow-sm border p-6 mb-6">
      <h3 class="text-lg font-semibold text-gray-800 mb-4">Upload Files</h3>
      
      <!-- Upload Area -->
      <div 
        @drop="handleDrop"
        @dragover.prevent
        @dragenter.prevent
        @dragleave="dragLeaveCounter--"
        @dragenter="dragLeaveCounter++"
        :class="[
          'upload-area border-2 border-dashed rounded-lg p-8 text-center transition-colors',
          isDragging ? 'border-blue-500 bg-blue-50' : 'border-gray-300'
        ]"
      >
        <div class="mb-4">
          <svg class="w-12 h-12 text-gray-400 mx-auto" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"/>
          </svg>
        </div>
        <p class="text-gray-600 mb-2">
          <span class="font-medium">Click to upload</span> or drag and drop files here
        </p>
        <p class="text-sm text-gray-500">Support for images, videos, documents and more</p>
        <input 
          ref="fileInput"
          type="file" 
          multiple 
          @change="handleFileSelect"
          class="hidden"
        >
        <button 
          @click="$refs.fileInput.click()"
          class="mt-4 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Select Files
        </button>
      </div>

      <!-- Upload Queue -->
      <div v-if="uploadQueue.length > 0" class="upload-queue mt-6">
        <h4 class="font-medium text-gray-800 mb-3">Upload Queue ({{ uploadQueue.length }} files)</h4>
        <div class="space-y-2">
          <div 
            v-for="(upload, index) in uploadQueue" 
            :key="index"
            class="upload-item flex items-center justify-between p-3 bg-gray-50 rounded-lg"
          >
            <div class="flex items-center flex-1">
              <div class="w-8 h-8 bg-blue-100 rounded flex items-center justify-center mr-3">
                <svg class="w-4 h-4 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </div>
              <div class="flex-1">
                <div class="font-medium text-gray-800">{{ upload.customKey || upload.file.name }}</div>
                <div class="text-sm text-gray-600">{{ formatFileSize(upload.file.size) }}</div>
                <div v-if="upload.progress !== undefined" class="w-full bg-gray-200 rounded-full h-2 mt-1">
                  <div 
                    class="bg-blue-600 h-2 rounded-full transition-all duration-300"
                    :style="{ width: upload.progress + '%' }"
                  ></div>
                </div>
              </div>
            </div>
            <div class="flex items-center space-x-2">
              <input 
                v-model="upload.customKey"
                type="text" 
                :placeholder="upload.file.name"
                class="px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                style="min-width: 150px;"
              >
              <button 
                @click="removeFromQueue(index)"
                :disabled="upload.uploading"
                class="text-red-600 hover:text-red-800 disabled:opacity-50"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"/>
                </svg>
              </button>
            </div>
          </div>
        </div>
        <div class="mt-4 flex gap-2">
          <button 
            @click="uploadFiles"
            :disabled="uploading || uploadQueue.every(u => u.uploading)"
            class="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {{ uploading ? 'Uploading...' : `Upload ${uploadQueue.length} Files` }}
          </button>
          <button 
            @click="clearQueue"
            :disabled="uploading"
            class="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            Clear Queue
          </button>
        </div>
      </div>
    </div>

    <!-- Loading State -->
    <div v-if="loading" class="loading-container">
      <div class="flex items-center justify-center py-12">
        <div class="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        <span class="ml-3 text-gray-600">Loading files...</span>
      </div>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="error-container">
      <div class="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
        <div class="flex items-center">
          <div class="text-red-600 mr-3">
            <svg class="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
              <path fill-rule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clip-rule="evenodd"/>
            </svg>
          </div>
          <div>
            <h3 class="text-red-800 font-medium">Error loading files</h3>
            <p class="text-red-700 text-sm mt-1">{{ error }}</p>
          </div>
        </div>
        <button @click="fetchFiles" class="mt-3 px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 transition-colors">
          Try Again
        </button>
      </div>
    </div>

    <!-- Files Grid -->
    <div v-else-if="processedFiles.length > 0" class="files-grid">
      <!-- Statistics Bar -->
      <div class="stats-bar bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div class="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div class="stat-item">
            <div class="text-2xl font-bold text-blue-600">{{ filesData.count || 0 }}</div>
            <div class="text-sm text-gray-600">Total Files</div>
          </div>
          <div class="stat-item">
            <div class="text-2xl font-bold text-green-600">{{ formatFileSize(filesData.totalSize || 0) }}</div>
            <div class="text-sm text-gray-600">Total Size</div>
          </div>
          <div class="stat-item">
            <div class="text-2xl font-bold text-purple-600">{{ getFileTypeCount('image') }}</div>
            <div class="text-sm text-gray-600">Images</div>
          </div>
          <div class="stat-item">
            <div class="text-2xl font-bold text-orange-600">{{ getFileTypeCount('video') }}</div>
            <div class="text-sm text-gray-600">Videos</div>
          </div>
        </div>
      </div>

      <!-- Filter Controls and Bulk Actions -->
      <div class="filter-controls bg-white rounded-lg shadow-sm border p-4 mb-6">
        <div class="flex flex-wrap gap-4 items-center justify-between">
          <div class="flex flex-wrap gap-4 items-center flex-1">
            <div class="flex-1 min-w-64">
              <input 
                v-model="searchQuery" 
                type="text" 
                placeholder="Search files by name..."
                class="w-full px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
            </div>
            <div>
              <select 
                v-model="filterType" 
                class="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              >
                <option value="">All Types</option>
                <option value="image">Images</option>
                <option value="video">Videos</option>
                <option value="audio">Audio</option>
                <option value="document">Documents</option>
              </select>
            </div>
            <div>
              <button 
                @click="refreshFiles" 
                class="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
              >
                <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"/>
                </svg>
                Refresh
              </button>
            </div>
          </div>
          
          <!-- Bulk Actions -->
          <div v-if="selectedFiles.length > 0" class="flex gap-2">
            <span class="text-sm text-gray-600 flex items-center">
              {{ selectedFiles.length }} selected
            </span>
            <button 
              @click="bulkDelete"
              :disabled="bulkOperationInProgress"
              class="px-3 py-2 bg-red-600 text-white text-sm rounded-lg hover:bg-red-700 disabled:opacity-50 transition-colors flex items-center"
            >
              <svg class="w-4 h-4 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
              </svg>
              Delete
            </button>
            <button 
              @click="clearSelection"
              class="px-3 py-2 bg-gray-600 text-white text-sm rounded-lg hover:bg-gray-700 transition-colors"
            >
              Clear
            </button>
          </div>
        </div>
      </div>

      <!-- Files Grid -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        <div 
          v-for="file in filteredFiles" 
          :key="file.key"
          class="file-card bg-white rounded-lg shadow-sm border hover:shadow-md transition-shadow duration-200"
          :class="{ 'ring-2 ring-blue-500': selectedFiles.includes(file.key) }"
        >
          <!-- Selection Checkbox -->
          <div class="absolute top-2 left-2 z-10">
            <input 
              type="checkbox"
              :checked="selectedFiles.includes(file.key)"
              @change="toggleFileSelection(file.key)"
              class="w-4 h-4 text-blue-600 bg-white border-gray-300 rounded focus:ring-blue-500"
            >
          </div>

          <!-- File Preview -->
          <div class="file-preview h-48 bg-gray-100 rounded-t-lg overflow-hidden relative">
            <img 
              v-if="isImage(file.key)"
              :src="file.fullUrl" 
              :alt="file.key"
              class="w-full h-full object-cover"
              @error="handleImageError"
            >
            <video 
              v-else-if="isVideo(file.key)"
              :src="file.fullUrl"
              class="w-full h-full object-cover"
              controls
              preload="metadata"
            >
              Your browser does not support the video tag.
            </video>
            <div 
              v-else 
              class="w-full h-full flex items-center justify-center bg-gray-200"
            >
              <div class="text-center">
                <svg class="w-12 h-12 text-gray-400 mx-auto mb-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
                <div class="text-sm text-gray-500">{{ getFileType(file.key) }}</div>
              </div>
            </div>
          </div>

          <!-- File Info -->
          <div class="file-info p-4">
            <div class="flex items-center justify-between mb-2">
              <h3 class="font-medium text-gray-900 truncate flex-1" :title="file.key">
                {{ file.key }}
              </h3>
              <button 
                @click="startRename(file.key)"
                class="text-gray-400 hover:text-gray-600 ml-2"
                title="Rename file"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                </svg>
              </button>
            </div>
            
            <!-- Rename Input -->
            <div v-if="renamingFile === file.key" class="mb-3">
              <input 
                v-model="newFileName"
                @keyup.enter="confirmRename(file.key)"
                @keyup.escape="cancelRename"
                type="text"
                class="w-full px-2 py-1 text-sm border border-gray-300 rounded focus:outline-none focus:ring-1 focus:ring-blue-500"
                ref="renameInput"
              >
              <div class="flex gap-1 mt-1">
                <button 
                  @click="confirmRename(file.key)"
                  class="px-2 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700"
                >
                  Save
                </button>
                <button 
                  @click="cancelRename"
                  class="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700"
                >
                  Cancel
                </button>
              </div>
            </div>

            <div class="text-sm text-gray-600 space-y-1">
              <div class="flex justify-between">
                <span>Size:</span>
                <span>{{ formatFileSize(file.size) }}</span>
              </div>
              <div class="flex justify-between">
                <span>Modified:</span>
                <span>{{ formatDate(file.lastModified) }}</span>
              </div>
              <div class="flex justify-between">
                <span>Type:</span>
                <span class="capitalize">{{ getFileType(file.key) }}</span>
              </div>
            </div>

            <!-- Action Buttons -->
            <div class="mt-4 flex gap-2">
              <button 
                @click="copyUrl(file.fullUrl)"
                class="flex-1 px-3 py-2 bg-blue-600 text-white text-sm rounded-md hover:bg-blue-700 transition-colors"
              >
                Copy URL
              </button>
              <button 
                @click="downloadFile(file.fullUrl, file.key)"
                class="px-3 py-2 bg-gray-600 text-white text-sm rounded-md hover:bg-gray-700 transition-colors"
                title="Download"
              >
                <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
                </svg>
              </button>
              <button 
                @click="deleteFile(file.key)"
                :disabled="deletingFiles.includes(file.key)"
                class="px-3 py-2 bg-red-600 text-white text-sm rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors"
                title="Delete"
              >
                <svg v-if="!deletingFiles.includes(file.key)" class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                </svg>
                <div v-else class="animate-spin rounded-full h-4 w-4 border-b-2 border-white"></div>
              </button>
            </div>
          </div>
        </div>
      </div>

      <!-- Pagination or Load More (if needed) -->
      <div v-if="filteredFiles.length < processedFiles.length" class="text-center mt-8">
        <button 
          @click="loadMore"
          class="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
        >
          Load More Files
        </button>
      </div>
    </div>

    <!-- Empty State -->
    <div v-else class="empty-state">
      <div class="text-center py-12">
        <svg class="w-24 h-24 text-gray-300 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"/>
        </svg>
        <h3 class="text-xl font-medium text-gray-900 mb-2">No files found</h3>
        <p class="text-gray-600 mb-4">Upload your first files to get started.</p>
        <button 
          @click="$refs.fileInput?.click()"
          class="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors"
        >
          Upload Files
        </button>
      </div>
    </div>

    <!-- Toast notifications -->
    <div 
      v-if="showToast.show" 
      :class="[
        'fixed bottom-4 right-4 px-4 py-2 rounded-lg shadow-lg transition-opacity z-50',
        showToast.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'
      ]"
    >
      {{ showToast.message }}
    </div>

    <!-- Loading Overlay for Operations -->
    <div v-if="bulkOperationInProgress" class="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div class="bg-white rounded-lg p-6 max-w-sm w-full mx-4">
        <div class="flex items-center">
          <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mr-3"></div>
          <span class="text-gray-800">Processing bulk operation...</span>
        </div>
      </div>
    </div>
    
    </div> <!-- End files-list-container -->
  </div> <!-- End app-layout -->
</template>

<script>
import axios from 'axios';
import Sidebar from './Sidebar.vue';

export default {
  name: 'FilesList',
  components: {
    Sidebar
  },
  data() {
    return {
      // Sidebar state
      sidebarCollapsed: true,
      
      loading: false,
      error: null,
      filesData: {},
      processedFiles: [],
      searchQuery: '',
      filterType: '',
      showToast: {
        show: false,
        message: '',
        type: 'success'
      },
      cdnBaseUrl: 'https://eu1.kewaarcdn.com',
      apiUrl: 'https://r2-eu1.kewaarcdn.com/api/files',
      
      // Upload functionality
      uploadQueue: [],
      uploading: false,
      isDragging: false,
      dragLeaveCounter: 0,
      
      // Selection and bulk operations
      selectedFiles: [],
      bulkOperationInProgress: false,
      deletingFiles: [],
      
      // Rename functionality
      renamingFile: null,
      newFileName: ''
    };
  },
  computed: {
    filteredFiles() {
      let files = this.processedFiles;

      // Apply search filter
      if (this.searchQuery) {
        const query = this.searchQuery.toLowerCase();
        files = files.filter(file => 
          file.key.toLowerCase().includes(query)
        );
      }

      // Apply type filter
      if (this.filterType) {
        files = files.filter(file => 
          this.getFileType(file.key) === this.filterType
        );
      }

      return files;
    }
  },
  watch: {
    dragLeaveCounter(newValue) {
      this.isDragging = newValue > 0;
    }
  },
  mounted() {
    this.fetchFiles();
    
    // Initialize sidebar state from localStorage
    const savedCollapsedState = localStorage.getItem('sidebarCollapsed');
    if (savedCollapsedState !== null) {
      this.sidebarCollapsed = savedCollapsedState === 'true';
    }
  },
  methods: {
    async fetchFiles() {
      this.loading = true;
      this.error = null;

      try {
        const response = await axios.get(this.apiUrl, {
          timeout: 30000,
          headers: {
            'Accept': 'application/json'
          }
        });

        if (response.data && response.data.success && response.data.data) {
          this.filesData = response.data.data;
          this.processFiles(response.data.data.objects || []);
        } else {
          throw new Error('Invalid response format from API');
        }
      } catch (err) {
        console.error('Error fetching files:', err);
        this.error = err.response?.data?.message || err.message || 'Failed to fetch files from the API';
      } finally {
        this.loading = false;
      }
    },

    processFiles(files) {
      this.processedFiles = files.map(file => ({
        ...file,
        key: file.Key,
        size: file.Size,
        lastModified: file.LastModified,
        etag: file.ETag,
        storageClass: file.StorageClass,
        fullUrl: `${this.cdnBaseUrl}/${file.Key}`
      }));
    },

    refreshFiles() {
      this.fetchFiles();
    },

    formatFileSize(bytes) {
      if (bytes === 0) return '0 B';
      const k = 1024;
      const sizes = ['B', 'KB', 'MB', 'GB', 'TB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
    },

    formatDate(dateString) {
      return new Date(dateString).toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    },

    getFileType(filename) {
      const extension = filename.split('.').pop()?.toLowerCase();
      
      const imageTypes = ['jpg', 'jpeg', 'png', 'gif', 'webp', 'svg', 'bmp'];
      const videoTypes = ['mp4', 'avi', 'mov', 'wmv', 'flv', 'webm', 'mkv'];
      const audioTypes = ['mp3', 'wav', 'ogg', 'aac', 'm4a', 'flac'];
      const documentTypes = ['pdf', 'doc', 'docx', 'txt', 'rtf'];

      if (imageTypes.includes(extension)) return 'image';
      if (videoTypes.includes(extension)) return 'video';
      if (audioTypes.includes(extension)) return 'audio';
      if (documentTypes.includes(extension)) return 'document';
      
      return 'other';
    },

    getFileTypeCount(type) {
      return this.processedFiles.filter(file => this.getFileType(file.key) === type).length;
    },

    isImage(filename) {
      return this.getFileType(filename) === 'image';
    },

    isVideo(filename) {
      return this.getFileType(filename) === 'video';
    },

    async copyUrl(url) {
      try {
        await navigator.clipboard.writeText(url);
        this.showToastMessage('URL copied to clipboard!', 'success');
      } catch (err) {
        // Fallback for older browsers
        const textArea = document.createElement('textarea');
        textArea.value = url;
        document.body.appendChild(textArea);
        textArea.select();
        try {
          document.execCommand('copy');
          this.showToastMessage('URL copied to clipboard!', 'success');
        } catch (fallbackErr) {
          console.error('Could not copy text: ', fallbackErr);
          this.showToastMessage('Failed to copy URL to clipboard', 'error');
        }
        document.body.removeChild(textArea);
      }
    },

    downloadFile(url, filename) {
      const link = document.createElement('a');
      link.href = url;
      link.download = filename;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },

    handleImageError(event) {
      // Hide broken images or show placeholder
      event.target.style.display = 'none';
    },

    loadMore() {
      // This method can be implemented if pagination is needed
      console.log('Load more functionality can be added here');
    },

    // Upload functionality
    handleDrop(event) {
      event.preventDefault();
      this.dragLeaveCounter = 0;
      this.isDragging = false;
      
      const files = Array.from(event.dataTransfer.files);
      this.addFilesToQueue(files);
    },

    handleFileSelect(event) {
      const files = Array.from(event.target.files);
      this.addFilesToQueue(files);
      // Reset the input
      event.target.value = '';
    },

    addFilesToQueue(files) {
      const newUploads = files.map(file => ({
        file,
        customKey: file.name,
        uploading: false,
        progress: undefined
      }));
      
      this.uploadQueue.push(...newUploads);
    },

    removeFromQueue(index) {
      this.uploadQueue.splice(index, 1);
    },

    clearQueue() {
      this.uploadQueue = [];
    },

    async uploadFiles() {
      if (this.uploadQueue.length === 0) return;
      
      this.uploading = true;
      const uploads = [...this.uploadQueue];
      
      for (let i = 0; i < uploads.length; i++) {
        const upload = uploads[i];
        if (upload.uploading) continue;
        
        try {
          upload.uploading = true;
          upload.progress = 0;
          
          const formData = new FormData();
          formData.append('file', upload.file);
          formData.append('key', upload.customKey || upload.file.name);
          
          await axios.post(`${this.apiUrl}/upload`, formData, {
            headers: {
              'Content-Type': 'multipart/form-data'
            },
            onUploadProgress: (progressEvent) => {
              upload.progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
            }
          });
          
          // Remove successful upload from queue
          const queueIndex = this.uploadQueue.findIndex(q => q === upload);
          if (queueIndex !== -1) {
            this.uploadQueue.splice(queueIndex, 1);
          }
          
        } catch (error) {
          console.error('Upload failed:', error);
          upload.uploading = false;
          upload.progress = undefined;
          this.showToastMessage(`Failed to upload ${upload.file.name}: ${error.response?.data?.message || error.message}`, 'error');
        }
      }
      
      this.uploading = false;
      
      // Refresh the files list if any uploads were successful
      if (this.uploadQueue.length < uploads.length) {
        this.showToastMessage('Files uploaded successfully!', 'success');
        await this.fetchFiles();
      }
    },

    // Selection functionality
    toggleFileSelection(fileKey) {
      const index = this.selectedFiles.indexOf(fileKey);
      if (index > -1) {
        this.selectedFiles.splice(index, 1);
      } else {
        this.selectedFiles.push(fileKey);
      }
    },

    clearSelection() {
      this.selectedFiles = [];
    },

    // Delete functionality
    async deleteFile(fileKey) {
      if (!confirm(`Are you sure you want to delete "${fileKey}"?`)) {
        return;
      }
      
      this.deletingFiles.push(fileKey);
      
      try {
        await axios.delete(`${this.apiUrl}/${encodeURIComponent(fileKey)}`);
        
        this.showToastMessage('File deleted successfully!', 'success');
        
        // Remove from selected files if it was selected
        const selectedIndex = this.selectedFiles.indexOf(fileKey);
        if (selectedIndex > -1) {
          this.selectedFiles.splice(selectedIndex, 1);
        }
        
        await this.fetchFiles();
      } catch (error) {
        console.error('Delete failed:', error);
        this.showToastMessage(`Failed to delete file: ${error.response?.data?.message || error.message}`, 'error');
      } finally {
        const index = this.deletingFiles.indexOf(fileKey);
        if (index > -1) {
          this.deletingFiles.splice(index, 1);
        }
      }
    },

    async bulkDelete() {
      if (this.selectedFiles.length === 0) return;
      
      if (!confirm(`Are you sure you want to delete ${this.selectedFiles.length} selected files?`)) {
        return;
      }
      
      this.bulkOperationInProgress = true;
      
      try {
        await axios.delete(`${this.apiUrl}/bulk`, {
          headers: {
            'Content-Type': 'application/json'
          },
          data: {
            keys: this.selectedFiles
          }
        });
        
        this.showToastMessage(`${this.selectedFiles.length} files deleted successfully!`, 'success');
        
        this.selectedFiles = [];
        await this.fetchFiles();
      } catch (error) {
        console.error('Bulk delete failed:', error);
        this.showToastMessage(`Failed to delete files: ${error.response?.data?.message || error.message}`, 'error');
      } finally {
        this.bulkOperationInProgress = false;
      }
    },

    // Rename functionality
    startRename(fileKey) {
      this.renamingFile = fileKey;
      this.newFileName = fileKey;
      
      // Focus the input in the next tick
      this.$nextTick(() => {
        const inputs = this.$refs.renameInput;
        if (inputs) {
          const input = Array.isArray(inputs) ? inputs[0] : inputs;
          if (input) {
            input.focus();
            input.select();
          }
        }
      });
    },

    cancelRename() {
      this.renamingFile = null;
      this.newFileName = '';
    },

    async confirmRename(oldKey) {
      if (!this.newFileName || this.newFileName === oldKey) {
        this.cancelRename();
        return;
      }
      
      try {
        await axios.put(`${this.apiUrl}/${encodeURIComponent(oldKey)}/rename`, {
          newKey: this.newFileName
        }, {
          headers: {
            'Content-Type': 'application/json'
          }
        });
        
        this.showToastMessage('File renamed successfully!', 'success');
        
        // Update selected files if the renamed file was selected
        const selectedIndex = this.selectedFiles.indexOf(oldKey);
        if (selectedIndex > -1) {
          this.selectedFiles.splice(selectedIndex, 1, this.newFileName);
        }
        
        this.cancelRename();
        await this.fetchFiles();
      } catch (error) {
        console.error('Rename failed:', error);
        this.showToastMessage(`Failed to rename file: ${error.response?.data?.message || error.message}`, 'error');
      }
    },

    // Toast functionality
    showToastMessage(message, type = 'success') {
      this.showToast.show = true;
      this.showToast.message = message;
      this.showToast.type = type;
      
      setTimeout(() => {
        this.showToast.show = false;
      }, 3000);
    },

    // Sidebar functionality
    handleSidebarCollapse(collapsed) {
      this.sidebarCollapsed = collapsed;
    }
  }
};
</script>

<style scoped>
.app-layout {
  min-height: 100vh;
  position: relative;
}

.files-list-container {
  min-height: 100vh;
  padding: 1rem;
  margin-left: 300px; /* Default sidebar width */
  transition: margin-left 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  background-color: #f8fafc;
  width: calc(100% - 300px);
}

.files-list-container.sidebar-collapsed {
  margin-left: 70px; /* Collapsed sidebar width */
  width: calc(100% - 70px);
}

.file-card {
  transition: transform 0.2s ease-in-out;
}

.file-card:hover {
  transform: translateY(-2px);
}

.file-preview img,
.file-preview video {
  transition: transform 0.3s ease;
}

.file-preview:hover img,
.file-preview:hover video {
  transform: scale(1.05);
}

.animate-spin {
  animation: spin 1s linear infinite;
}

@keyframes spin {
  from {
    transform: rotate(0deg);
  }
  to {
    transform: rotate(360deg);
  }
}

/* Upload area styling */
.upload-area {
  cursor: pointer;
}

.upload-area:hover {
  border-color: #3b82f6;
  background-color: #f8fafc;
}

/* File card selection styling */
.file-card {
  position: relative;
}

.file-card input[type="checkbox"] {
  z-index: 10;
}

/* Progress bar styling */
.upload-item .bg-blue-600 {
  transition: width 0.3s ease;
}

/* Rename input styling */
.file-info input[type="text"] {
  font-size: 0.875rem;
}

/* Toast positioning */
.fixed.bottom-4.right-4 {
  z-index: 1000;
}

/* Loading overlay */
.fixed.inset-0 {
  backdrop-filter: blur(2px);
}

/* Desktop layout adjustments */
@media (min-width: 769px) {
  .app-layout {
    padding-left: 0; /* Let the sidebar handle its own positioning */
  }
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .files-list-container {
    margin-left: 0;
    padding: 4rem 0.5rem 1rem; /* Add top padding for mobile toggle button */
    width: 100%;
  }
  
  .app-layout {
    position: relative;
  }
  
  .filter-controls {
    flex-direction: column;
  }
  
  .filter-controls > div {
    width: 100%;
  }
  
  .stats-bar .grid {
    grid-template-columns: repeat(2, 1fr);
  }
  
  .upload-area {
    padding: 2rem 1rem;
  }
  
  .upload-item {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }
  
  .upload-item .flex.items-center.space-x-2 {
    justify-content: space-between;
    margin-top: 0.5rem;
  }
}

@media (max-width: 480px) {
  .stats-bar .grid {
    grid-template-columns: 1fr;
  }
  
  .file-card .file-info .mt-4.flex.gap-2 {
    flex-direction: column;
    gap: 0.5rem;
  }
  
  .filter-controls .flex.gap-2 {
    flex-direction: column;
    width: 100%;
  }
}
</style> 